/**
 * InfoDot — shared types, Zod prop schema, and styling tokens.
 *
 * A small circular (i) toggle placed beside a label. Activating it expands the
 * card it sits in, in place, to reveal a description; it never opens a popover
 * or tooltip. The component is controlled: the host owns `expanded`, renders
 * the region named by `controls`, and flips state on toggle.
 *
 * Web only — the native build renders nothing.
 *
 * Source of truth: web registration v7 sheet, Web/Info dot
 * https://www.figma.com/design/hk9NaJrKRwkoGmmZ5rxbsE (node 69:24)
 */

import { z } from 'zod';
import { actionIdSchema } from '@galapagos/product-analytics';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import type { IconName } from '../../elements/icons/registry';

/** Catalog-facing schema (plain ZodObject so `formatZodType` can introspect it). */
export const infoDotPropsSchema = z.object({
  /** Accessible name, e.g. "More about alcohol". Required: the dot has no visible text. */
  accessibilityLabel: z.string().min(1),
  /** Whether the region it controls is currently shown. */
  expanded: z.boolean().default(false),
  /** Id of the region this dot shows and hides. */
  controls: z.string().optional(),
  /** Analytics id; omitted falls back to the generic `ui.info-dot.toggle`. */
  actionId: actionIdSchema.optional(),
  testID: z.string().optional(),
});

export type InfoDotProps = z.input<typeof infoDotPropsSchema>;

// Figma draws the disc at a raw 24px with no bound variable; the icon scale's
// `sm` step carries the same value.
export const INFO_DOT_DISC_SIZE = sizing.icon.sm;
/** Invisible tappable area around the disc. */
export const INFO_DOT_HIT_AREA = sizing.controlButton.md;
/** How far the tappable area reaches past the disc on each side. */
export const INFO_DOT_HIT_AREA_OUTSET = (INFO_DOT_HIT_AREA - INFO_DOT_DISC_SIZE) / 2;
export const INFO_DOT_ICON_SIZE = sizing.icon.xs;
export const INFO_DOT_RADIUS = radius.rounded;
export const INFO_DOT_ICON: IconName = 'info';

export const INFO_DOT_ICON_COLOR = colors.icon.secondary;
/** Hover and open: the disc fills and the glyph darkens. */
export const INFO_DOT_ACTIVE_BACKGROUND = colors.button.secondary;
export const INFO_DOT_ACTIVE_ICON_COLOR = colors.icon.primary;
